import React, { useEffect, useRef, useState } from "react";
import { Upload, AlertTriangle, Check, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtNOK, fmtPct } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, Card } from "@/components/common";
import { cn } from "@/lib/utils";

function NumCell({ value, onCommit, step = "1", className, testId, suffix }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div className="flex items-center justify-end gap-1">
      <Input type="number" step={step} value={v ?? ""} onChange={(e) => setV(e.target.value)} onBlur={() => Number(v) !== Number(value) && onCommit(Number(v))} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} className={cn("h-8 w-24 rounded-[8px] text-right tabular", className)} data-testid={testId} />
      {suffix ? <span className="text-xs text-muted-foreground">{suffix}</span> : null}
    </div>
  );
}

export function CalculationTab({ data, reload, setTab }) {
  const o = data.opportunity;
  const solutions = data.solutions || [];
  const [selId, setSelId] = useState((solutions.find((s) => s.is_active) || solutions[0])?.id);
  const sol = solutions.find((s) => s.id === selId) || solutions.find((s) => s.is_active) || solutions[0];
  const [suppliers, setSuppliers] = useState({});
  const [parsed, setParsed] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(Object.fromEntries(r.data.map((s) => [s.id, s.name])))).catch(() => {});
  }, []);

  const patchItem = async (itemId, body) => {
    try {
      await api.patch(`/solutions/${sol.id}/items/${itemId}`, body);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const uploadSupplierQuote = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/opportunities/${o.id}/supplier-quote`, fd);
      setParsed(r.data);
      toast.success(`Found ${r.data.lines.length} product lines`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const applyCosts = async () => {
    try {
      const r = await api.post(`/solutions/${sol.id}/apply-supplier-costs`, { lines: parsed.lines.filter((l) => l.matched_product_id) });
      toast.success(`Calculation updated - GM now ${fmtPct(r.data.totals.gm_pct)}`);
      setParsed(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!sol) {
    return <EmptyState title="No solution to calculate" description="Build or generate a solution first." action={<Button onClick={() => setTab("solution")} className="bg-primary text-white hover:bg-[#ff8e76]">Go to solution</Button>} testId="calculation-empty" />;
  }
  const t = sol.totals;
  const a = t.approval;

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-[-0.02em]">Calculation</h2>
          <p className="mt-1 text-sm text-muted-foreground">Edit quantities, costs, prices and discounts inline. Totals and margin update live.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {solutions.length > 1 && (
            <div className="flex rounded-full border bg-white p-0.5">
              {solutions.map((s) => <button key={s.id} type="button" onClick={() => setSelId(s.id)} className={cn("rounded-full px-3 py-1 text-xs font-medium", sol.id === s.id ? "bg-foreground text-white" : "text-muted-foreground hover:text-foreground")} data-testid={`calculation-variant-${s.id}`}>{s.name}</button>)}
            </div>
          )}
          <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx,.txt,.csv" className="hidden" onChange={(e) => { uploadSupplierQuote(e.target.files?.[0]); e.target.value = ""; }} data-testid="calculation-supplier-quote-input" />
          <Button variant="outline" className="gap-2" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="calculation-import-supplier-quote-button"><Truck className="h-4 w-4" /> {uploading ? "Reading quote..." : "Import supplier quote"}</Button>
        </div>
      </div>

      <div className={cn("mt-5 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm", a.required ? "border-[hsl(var(--status-warn)/0.3)] bg-[hsl(var(--status-warn)/0.06)]" : "border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)]")} data-testid="calculation-approval-banner">
        {a.required ? <AlertTriangle className="mt-0.5 h-4 w-4 text-[hsl(var(--status-warn))]" /> : <Check className="mt-0.5 h-4 w-4 text-[hsl(var(--status-ok))]" />}
        <div><span className="font-medium">{a.label}</span>{a.reason ? <span className="text-muted-foreground"> – {a.reason}</span> : <span className="text-muted-foreground"> – GM {fmtPct(t.gm_pct)} is above the 30% threshold.</span>}</div>
      </div>

      <Card className="mt-5 overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader className="sticky top-0 bg-white">
            <TableRow>
              <TableHead className="text-xs">Product</TableHead>
              <TableHead className="w-24 text-right text-xs">Qty</TableHead>
              <TableHead className="w-32 text-right text-xs">Cost</TableHead>
              <TableHead className="w-32 text-right text-xs">Sales price</TableHead>
              <TableHead className="w-28 text-right text-xs">Discount</TableHead>
              <TableHead className="w-24 text-right text-xs">Margin</TableHead>
              <TableHead className="w-32 text-right text-xs">Line total</TableHead>
              <TableHead className="w-36 text-xs">Supplier</TableHead>
              <TableHead className="w-20 text-right text-xs">Lead</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sol.groups.map((g) => {
              const gt = t.groups.find((x) => x.group_id === g.id);
              return (
                <React.Fragment key={g.id}>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell colSpan={6} className="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.name}</TableCell>
                    <TableCell className="py-2 text-right text-xs tabular font-medium">{gt ? fmtNOK(gt.sales) : ""}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                  {g.items.map((it) => {
                    const sales = it.qty * it.sales_price * (1 - (it.discount_pct || 0) / 100);
                    const cost = it.qty * it.cost_price;
                    const m = sales ? ((sales - cost) / sales) * 100 : 0;
                    return (
                      <TableRow key={it.id} className="h-12" data-testid={`calculation-row-${it.id}`}>
                        <TableCell><div className="font-medium">{it.name}</div><div className="text-xs text-muted-foreground">{it.manufacturer} · {it.sku}</div></TableCell>
                        <TableCell><NumCell value={it.qty} onCommit={(v) => patchItem(it.id, { qty: v })} className="w-16" testId="calculation-qty-input" /></TableCell>
                        <TableCell><NumCell value={it.cost_price} onCommit={(v) => patchItem(it.id, { cost_price: v })} testId="calculation-cost-input" /></TableCell>
                        <TableCell><NumCell value={it.sales_price} onCommit={(v) => patchItem(it.id, { sales_price: v })} testId="calculation-sales-input" /></TableCell>
                        <TableCell><NumCell value={it.discount_pct || 0} onCommit={(v) => patchItem(it.id, { discount_pct: v })} className="w-16" suffix="%" testId="calculation-discount-input" /></TableCell>
                        <TableCell className={cn("text-right tabular", m < 20 ? "text-[hsl(var(--status-bad))]" : m < 30 ? "text-[hsl(var(--status-warn))]" : "")}>{fmtPct(m)}</TableCell>
                        <TableCell className="text-right tabular font-medium">{fmtNOK(sales)}</TableCell>
                        <TableCell className="truncate text-xs text-muted-foreground">{suppliers[it.supplier_id] || ""}</TableCell>
                        <TableCell className="text-right text-xs tabular text-muted-foreground">{it.is_service ? "–" : `${it.lead_time_weeks}w`}</TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        <div className="grid grid-cols-2 gap-4 border-t bg-muted/30 px-5 py-4 text-sm sm:grid-cols-4 lg:grid-cols-7" data-testid="calculation-totals">
          <Tot label="Hardware" v={fmtNOK(t.hardware)} />
          <Tot label="Software" v={fmtNOK(t.software)} />
          <Tot label="Services" v={fmtNOK(t.services)} />
          <Tot label="Total sales" v={fmtNOK(t.total_sales)} bold testId="calculation-total-sales" />
          <Tot label="Cost" v={fmtNOK(t.total_cost)} />
          <Tot label="Gross margin" v={fmtNOK(t.gross_margin)} />
          <Tot label="GM %" v={fmtPct(t.gm_pct)} bold className={t.gm_pct < 30 ? "text-[hsl(var(--status-warn))]" : "text-[hsl(var(--status-ok))]"} testId="calculation-gm-percent-value" />
        </div>
      </Card>
      <p className="mt-3 text-xs text-muted-foreground">All prices in NOK excl. VAT. Cost and list prices are demo data.</p>

      <Dialog open={!!parsed} onOpenChange={(v) => !v && setParsed(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="supplier-quote-dialog">
          <DialogHeader><DialogTitle>Supplier quote{parsed?.supplier ? ` – ${parsed.supplier}` : ""}</DialogTitle><DialogDescription>I found {parsed?.lines?.length || 0} product lines. Matched lines can update the cost prices in this calculation.</DialogDescription></DialogHeader>
          {parsed && (
            <div className="max-h-[50vh] overflow-y-auto rounded-xl border">
              <Table>
                <TableHeader><TableRow><TableHead className="text-xs">Supplier item</TableHead><TableHead className="text-xs">Matched product</TableHead><TableHead className="text-right text-xs">Qty</TableHead><TableHead className="text-right text-xs">Cost</TableHead></TableRow></TableHeader>
                <TableBody>
                  {parsed.lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">{l.supplier_item}</TableCell>
                      <TableCell className="text-sm">{l.matched_product_name ? <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-[hsl(var(--status-ok))]" />{l.matched_product_name}</span> : <span className="text-[hsl(var(--status-warn))]">Not matched</span>}</TableCell>
                      <TableCell className="text-right tabular">{l.qty}</TableCell>
                      <TableCell className="text-right tabular">{fmtNOK(l.unit_cost)}{l.current_cost != null && l.unit_cost != null && <span className="ml-1 text-xs text-muted-foreground">(was {fmtNOK(l.current_cost)})</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setParsed(null)}>Close</Button><Button onClick={applyCosts} className="gap-2 bg-primary text-white hover:bg-[#ff8e76]" data-testid="supplier-quote-apply-button"><Check className="h-4 w-4" /> Update calculation</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tot({ label, v, bold, className, testId }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn("tabular", bold ? "text-base font-semibold" : "font-medium", className)} data-testid={testId}>{v}</div>
    </div>
  );
}
